import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { projectAccountService, CreateProjectAccountData, UpdateProjectAccountData } from '@/services/projectAccountService';
import { projectService } from '@/services/projectService';
import { ProjectAccount, ProjectAccountType } from '@/types';
import { Toast } from '@/components/Toast';
import { AdminSidebar } from '@/components/Layout/AdminSidebar';
import { useTheme } from '@/hooks/useTheme';

const projectAccountSchema = z.object({
  login: z.string().min(1, 'Le login est requis'),
  password: z.string().min(1, 'Le mot de passe est requis'),
  type: z.string().min(1, 'Le type est requis'),
});

type ProjectAccountFormData = z.infer<typeof projectAccountSchema>;


export function ProjectAccountsPage() {
  const { projectId } = useParams<{ projectId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { isDark, toggleTheme } = useTheme();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [editingAccount, setEditingAccount] = useState<ProjectAccount | null>(null);
  const [showPassword, setShowPassword] = useState(false);

  const projectIdNum = projectId ? parseInt(projectId, 10) : 0;

  const {
    register,
    handleSubmit,
    control,
    reset,
    formState: { errors },
    setValue,
  } = useForm<ProjectAccountFormData>({
    resolver: zodResolver(projectAccountSchema),
  });

  // Fetch project
  const { data: project } = useQuery({
    queryKey: ['project', projectIdNum],
    queryFn: () => projectService.getProject(projectIdNum),
    enabled: !!projectIdNum,
  });

  // Fetch project accounts
  const {
    data: projectAccounts,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['projectAccounts', projectIdNum],
    queryFn: () => projectAccountService.getProjectAccounts(projectIdNum),
    enabled: !!projectIdNum,
  });

  // Fetch project account types
  const { data: projectAccountTypes } = useQuery({
    queryKey: ['projectAccountTypes'],
    queryFn: () => projectAccountService.getProjectAccountTypes(),
  });

  const [toastMessage, setToastMessage] = useState<string>('');
  const [toastType, setToastType] = useState<'success' | 'error'>('success');
  const [showToast, setShowToast] = useState(false);

  // Create project account mutation
  const createMutation = useMutation({
    mutationFn: (data: CreateProjectAccountData) => projectAccountService.createProjectAccount(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['projectAccounts', projectIdNum] });
      setToastMessage('Compte IoT ajouté avec succès !');
      setToastType('success');
      setShowToast(true);
      setIsSidebarOpen(false);
      reset();
      setEditingAccount(null);
      setShowPassword(false);
    },
    onError: () => {
      setToastMessage('Erreur lors de l\'ajout du compte IoT');
      setToastType('error');
      setShowToast(true);
    },
  });

  // Update project account mutation
  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: UpdateProjectAccountData }) =>
      projectAccountService.updateProjectAccount(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['projectAccounts', projectIdNum] });
      setToastMessage('Compte IoT modifié avec succès !');
      setToastType('success');
      setShowToast(true);
      setIsSidebarOpen(false);
      reset();
      setEditingAccount(null);
      setShowPassword(false);
    },
    onError: () => {
      setToastMessage('Erreur lors de la modification du compte IoT');
      setToastType('error');
      setShowToast(true);
    },
  });

  // Delete project account mutation
  const deleteMutation = useMutation({
    mutationFn: (id: number) => projectAccountService.deleteProjectAccount(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['projectAccounts', projectIdNum] });
      setToastMessage('Compte IoT supprimé avec succès !');
      setToastType('success');
      setShowToast(true);
    },
    onError: () => {
      setToastMessage('Erreur lors de la suppression du compte IoT');
      setToastType('error');
      setShowToast(true);
    },
  });

  const handleNewAccount = () => {
    setEditingAccount(null);
    reset({
      login: '',
      password: '',
      type: '',
    });
    setShowPassword(false);
    setIsSidebarOpen(true);
  };

  const handleEdit = (account: ProjectAccount) => {
    setEditingAccount(account);
    setValue('login', account.login);
    setValue('type', account.type ? `/api/project_account_types/${account.type.id}` : '');
    setValue('password', account.password || ''); // Pre-fill password if available
    setShowPassword(false);
    setIsSidebarOpen(true);
  };

  const handleDelete = (id: number) => {
    if (window.confirm('Êtes-vous sûr de vouloir supprimer ce compte IoT ?')) {
      deleteMutation.mutate(id);
    }
  };

  const onSubmit = (data: ProjectAccountFormData) => {
    if (editingAccount) {
      const updateData: UpdateProjectAccountData = {
        login: data.login,
        password: data.password,
        type: data.type,
        project: `/api/projects/${projectIdNum}`,
      };
      updateMutation.mutate({ id: editingAccount.id, data: updateData });
    } else {
      const accountData: CreateProjectAccountData = {
        login: data.login,
        password: data.password,
        type: data.type,
        project: `/api/projects/${projectIdNum}`,
      };
      createMutation.mutate(accountData);
    }
  };

  const getUserInitials = (name: string) => {
    return name
      .split(' ')
      .map((n) => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  return (
    <div className="flex h-screen overflow-hidden bg-slate-50 dark:bg-[#0B1116] text-slate-800 dark:text-slate-200 transition-colors duration-200">
      <AdminSidebar getUserInitials={getUserInitials} />

      <main className="flex-1 flex flex-col min-w-0 overflow-hidden bg-slate-50 dark:bg-[#0B1116]">
        <header className="flex items-center justify-between h-16 px-6 border-b border-slate-200 dark:border-[#1E293B] bg-white dark:bg-[#16202A]">
          <div className="flex items-center gap-4">
            <div className="text-sm text-slate-500 dark:text-slate-400">
              <span className="text-slate-400 dark:text-slate-600">Admin</span> / Comptes IoT
            </div>
            {project && (
              <div className="flex items-center gap-2">
                <span className="text-lg font-bold text-slate-900 dark:text-white">{project.title}</span>
                {project.city && (
                  <>
                    <span className="text-slate-400">•</span>
                    <span className="text-sm text-slate-500 dark:text-slate-400">{project.city}</span>
                  </>
                )}
              </div>
            )}
          </div>
          <div className="flex items-center gap-4">
            <button
              onClick={handleNewAccount}
              className="flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors"
            >
              <span className="material-icons-outlined">add</span>
              <span>Nouveau compte IoT</span>
            </button>
            <button
              onClick={toggleTheme}
              className="p-2 text-slate-400 hover:text-slate-500 dark:hover:text-white transition-colors"
            >
              {isDark ? (
                <span className="material-icons-outlined">light_mode</span>
              ) : (
                <span className="material-icons-outlined">dark_mode</span>
              )}
            </button>
          </div>
        </header>

        <div className="flex-1 overflow-y-auto p-6 md:p-8">
          {isLoading ? (
            <div className="text-center py-12">Chargement...</div>
          ) : error ? (
            <div className="text-center py-12 text-red-500">
              <div>Erreur lors du chargement des comptes IoT</div>
              {error instanceof Error && (
                <div className="text-xs mt-2 text-red-400">{error.message}</div>
              )}
            </div>
          ) : !projectAccounts || projectAccounts.length === 0 ? (
            <div className="text-center py-12 text-slate-500">
              Aucun compte IoT trouvé
              {projectIdNum && (
                <div className="text-xs mt-2 text-slate-400">
                  Projet ID: {projectIdNum} - Vérifiez que des comptes existent pour ce projet dans la base de données
                </div>
              )}
            </div>
          ) : (
            <div className="bg-white dark:bg-[#16202A] rounded-lg border border-slate-200 dark:border-[#1E293B] overflow-hidden">
              <table className="w-full">
                <thead className="bg-slate-50 dark:bg-[#1E293B]">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                      Login
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                      Type
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-200 dark:divide-[#1E293B]">
                  {projectAccounts.map((account) => (
                    <tr key={account.id} className="hover:bg-slate-50 dark:hover:bg-[#1E293B]">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium">{account.login}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="px-2 py-1 text-xs rounded bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200">
                          {account.type?.name || 'Non défini'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex items-center justify-end gap-2">
                          <button
                            onClick={() => handleEdit(account)}
                            className="text-primary hover:text-primary/80 transition-colors"
                            title="Modifier"
                          >
                            <span className="material-icons-outlined">edit</span>
                          </button>
                          <button
                            onClick={() => handleDelete(account.id)}
                            className="text-red-500 hover:text-red-700 transition-colors"
                            title="Supprimer"
                          >
                            <span className="material-icons-outlined">delete</span>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </main>

      {/* Sidebar */}
      {isSidebarOpen && (
        <>
          {/* Backdrop */}
          <div
            className="fixed inset-0 bg-black/50 z-40"
            onClick={() => {
              setIsSidebarOpen(false);
              reset();
              setEditingAccount(null);
              setShowPassword(false);
            }}
          />
          {/* Sidebar */}
          <div className="fixed inset-y-0 right-0 w-[600px] bg-white dark:bg-[#16202A] border-l border-slate-200 dark:border-[#1E293B] shadow-2xl z-50 flex flex-col overflow-hidden">
            {/* Header */}
            <div className="px-6 py-4 border-b border-slate-200 dark:border-[#1E293B] flex items-center justify-between bg-slate-50 dark:bg-[#0B1116]">
              <h2 className="text-lg font-semibold text-slate-900 dark:text-white">
                {editingAccount ? 'Modifier le compte IoT' : 'Nouveau compte IoT'}
              </h2>
              <button
                onClick={() => {
                  setIsSidebarOpen(false);
                  reset();
                  setEditingAccount(null);
                  setShowPassword(false);
                }}
                className="text-slate-400 hover:text-slate-600 dark:hover:text-white transition-colors"
              >
                <span className="material-icons-outlined">close</span>
              </button>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto p-6">
              <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-2 text-slate-700 dark:text-slate-300">
                    Login
                  </label>
                  <input
                    {...register('login')}
                    type="text"
                    className="w-full px-3 py-2 border border-slate-300 dark:border-[#1E293B] rounded-lg bg-white dark:bg-[#16202A] text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary"
                  />
                  {errors.login && <p className="text-red-500 text-xs mt-1">{errors.login.message}</p>}
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2 text-slate-700 dark:text-slate-300">
                    Mot de passe
                    <span className="text-red-500 ml-1">*</span>
                  </label>
                  <div className="relative">
                    <input
                      {...register('password')}
                      type={showPassword ? 'text' : 'password'}
                      placeholder={editingAccount ? 'Nouveau mot de passe' : 'Mot de passe'}
                      className="w-full px-3 py-2 pr-10 border border-slate-300 dark:border-[#1E293B] rounded-lg bg-white dark:bg-[#16202A] text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary placeholder:text-slate-400 dark:placeholder:text-slate-500"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 dark:hover:text-white transition-colors"
                    >
                      {showPassword ? (
                        <span className="material-icons-outlined">visibility_off</span>
                      ) : (
                        <span className="material-icons-outlined">visibility</span>
                      )}
                    </button>
                  </div>
                  {errors.password && <p className="text-red-500 text-xs mt-1">{errors.password.message}</p>}
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2 text-slate-700 dark:text-slate-300">
                    Type
                  </label>
                  <Controller
                    name="type"
                    control={control}
                    render={({ field }) => (
                      <select
                        {...field}
                        className="w-full px-3 py-2 border border-slate-300 dark:border-[#1E293B] rounded-lg bg-white dark:bg-[#16202A] text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary"
                      >
                        <option value="">Sélectionner un type</option>
                        {projectAccountTypes?.map((type) => (
                          <option key={type.id} value={`/api/project_account_types/${type.id}`}>
                            {type.name} ({type.code})
                          </option>
                        ))}
                      </select>
                    )}
                  />
                  {errors.type && <p className="text-red-500 text-xs mt-1">{errors.type.message}</p>}
                </div>

                <div className="flex gap-3 pt-4 border-t border-slate-200 dark:border-[#1E293B]">
                  <button
                    type="button"
                    onClick={() => {
                      setIsSidebarOpen(false);
                      reset();
                      setEditingAccount(null);
                      setShowPassword(false);
                    }}
                    className="flex-1 px-4 py-2 border border-slate-300 dark:border-[#1E293B] rounded-lg hover:bg-slate-50 dark:hover:bg-[#1E293B] transition-colors text-slate-700 dark:text-slate-300"
                  >
                    Annuler
                  </button>
                  <button
                    type="submit"
                    disabled={createMutation.isPending || updateMutation.isPending}
                    className="flex-1 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors disabled:opacity-50"
                  >
                    {createMutation.isPending || updateMutation.isPending ? 'Enregistrement...' : 'Enregistrer'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </>
      )}

      {showToast && (
        <Toast
          message={toastMessage}
          type={toastType}
          onClose={() => setShowToast(false)}
        />
      )}
    </div>
  );
}
